/**
 * The catalog, as a table you can reorder.
 *
 * A React Aria `Table` rather than a stack of cards, because that is what this is: 26 rows of
 * the same seven facts, scanned down a column. Sorting is the admin's `order` field, and
 * dragging a row rewrites it — so the sequence a parent sees in the Studio's picker and the
 * sequence here are the same object, edited directly.
 *
 * The table is the one control the design system does not carry, and deliberately so: Quiet
 * Paper is the **story world's** look, and an internal data grid is not part of it. So this is
 * the one place the admin app reaches React Aria itself, and the classes it uses (`.ctable`
 * and friends) live in this app's own small sheet over the design system's tokens.
 *
 * Reordering writes `order` for every row whose position actually moved, 1-based, matching
 * the seed. A drag is not a save button: it is the edit.
 */

import { Button, Chip, EmptyState, StatusBadge, SurfaceHeader } from '@atlas-nook/design-system';
import { attentionReasons } from '@atlas-nook/mediums';
import type { Medium } from '@atlas-nook/mediums';
import { useState } from 'react';
import {
  Button as AriaButton,
  Cell,
  Column,
  Row,
  Table,
  TableBody,
  TableHeader,
  useDragAndDrop,
} from 'react-aria-components';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';

import { useCatalog } from '../data/queries.js';
import { writeMediumOrder } from '../data/writes.js';
import { useToast } from '../shell/ToastProvider.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { AVAILABILITY } from './options.js';
import { NewMediumDialog } from './NewMediumDialog.js';

/**
 * Where a medium stands on the shelf, as one word plus its badge.
 *
 * "Published" is not always the whole truth. The publish gate (PRD 03 §4.4) only runs on the
 * way in, so rows that went up before it exists sit on the shelf without the price the store
 * is meant to display — and a missing `formatLabel` is quieter still, because the store falls
 * back to the family and shows a plausible wrong chip. Both are said here, in the column an
 * admin actually scans, rather than only on the record's own page.
 */
function shelfState(medium: Medium): { label: string; status: 'ready' | 'needs-ok' | 'none' } {
  if (medium.published) {
    const reasons = attentionReasons(medium);
    return reasons.length === 0
      ? { label: S.catalog.state.published, status: 'ready' }
      : { label: S.catalog.state.needs(reasons), status: 'needs-ok' };
  }
  if (medium.comingSoon === true) return { label: S.catalog.state.comingSoon, status: 'needs-ok' };
  return { label: S.catalog.state.hidden, status: 'none' };
}

/** Splice-move semantics, then renumber from 1 — the same numbering the seed writes. */
export function reorderedOrders(
  rows: { id: string; order: number }[],
  from: number,
  to: number,
): { id: string; order: number }[] {
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [];
  next.splice(to, 0, moved);

  return next
    .map((row, index) => ({ id: row.id, order: index + 1 }))
    .filter((row, index) => next[index]?.order !== row.order);
}

export function CatalogList(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: catalog, loading } = useCatalog();
  const [creating, setCreating] = useState(false);
  // One filter, two chips: they are mutually exclusive views of the same list, and pressing
  // either releases the other.
  const [filter, setFilter] = useState<'published' | 'attention' | null>(null);

  const needsAttention = (medium: Medium): boolean => attentionReasons(medium).length > 0;
  const published = catalog.filter((medium) => medium.published).length;
  const attention = catalog.filter(needsAttention).length;
  const rows =
    filter === 'published'
      ? catalog.filter((medium) => medium.published)
      : filter === 'attention'
        ? catalog.filter(needsAttention)
        : catalog;

  const { dragAndDropHooks } = useDragAndDrop({
    getItems: (keys) => [...keys].map((key) => ({ 'text/plain': String(key) })),
    onReorder(event) {
      // Reordering a filtered view would renumber against a sequence that is not the whole
      // catalog, so it is only offered on the full list.
      if (filter !== null) return;

      const ids = catalog.map((medium) => ({ id: medium.id, order: medium.order }));
      const from = ids.findIndex((row) => row.id === String([...event.keys][0]));
      const target = ids.findIndex((row) => row.id === String(event.target.key));
      if (from === -1 || target === -1) return;

      const insertAt = event.target.dropPosition === 'after' ? target + 1 : target;
      const to = insertAt > from ? insertAt - 1 : insertAt;
      if (to === from) return;

      void writeMediumOrder(reorderedOrders(ids, from, to)).catch(() =>
        toast.show(S.common.failed),
      );
    },
  });

  return (
    <>
      <SurfaceHeader
        kicker={S.catalog.kicker}
        title={S.catalog.title}
        meta={S.catalog.meta(catalog.length, published, attention)}
        actions={
          <Button tone="primary" onClick={() => setCreating(true)}>
            {S.catalog.newMedium}
          </Button>
        }
      />

      <div className="chiprow" role="group" aria-label={S.catalog.tableLabel}>
        <Chip
          pressed={filter === 'published'}
          count={published}
          onClick={() => setFilter((was) => (was === 'published' ? null : 'published'))}
        >
          {S.catalog.state.published}
        </Chip>
        <Chip
          pressed={filter === 'attention'}
          count={attention}
          onClick={() => setFilter((was) => (was === 'attention' ? null : 'attention'))}
        >
          {S.catalog.needsAttention}
        </Chip>
      </div>

      {!loading && catalog.length === 0 ? (
        <EmptyState title={S.catalog.empty} />
      ) : (
        <div className="ctable-wrap">
          <Table
            className="ctable"
            aria-label={S.catalog.tableLabel}
            selectionMode="none"
            dragAndDropHooks={dragAndDropHooks}
          >
            <TableHeader>
              <Column id="order" isRowHeader className="ch ch-num">
                {S.catalog.columns.order}
              </Column>
              <Column id="format" className="ch">
                {S.catalog.columns.format}
              </Column>
              <Column id="name" className="ch">
                {S.catalog.columns.name}
              </Column>
              <Column id="tier" className="ch">
                {S.catalog.columns.tier}
              </Column>
              <Column id="layout" className="ch">
                {S.catalog.columns.layout}
              </Column>
              <Column id="availability" className="ch">
                {S.catalog.columns.availability}
              </Column>
              <Column id="state" className="ch">
                {S.catalog.columns.state}
              </Column>
            </TableHeader>

            <TableBody>
              {rows.map((medium) => {
                const state = shelfState(medium);
                return (
                  <Row key={medium.id} id={medium.id} className="crow">
                    <Cell className="cc cc-num">
                      <AriaButton
                        slot="drag"
                        className="cgrip"
                        aria-label={S.catalog.reorderLabel(medium.name.en)}
                      >
                        <GripGlyph />
                      </AriaButton>
                      <span>{medium.order}</span>
                    </Cell>
                    <Cell className="cc cc-mono">{medium.formatLabel?.en ?? S.common.none}</Cell>
                    <Cell className="cc">
                      <AriaButton
                        className="clink"
                        onPress={() => void navigate(`/mediums/${medium.id}`)}
                      >
                        {medium.name.en}
                      </AriaButton>
                      <span className="cc-slug">{medium.id}</span>
                    </Cell>
                    <Cell className="cc cc-mono">{S.catalog.tierValue(medium.tier)}</Cell>
                    <Cell className="cc cc-mono">
                      {medium.composition === undefined ? S.common.none : medium.composition.layout}
                    </Cell>
                    <Cell className="cc cc-mono">{AVAILABILITY[medium.availability]}</Cell>
                    <Cell className="cc">
                      <StatusBadge status={state.status}>{state.label}</StatusBadge>
                    </Cell>
                  </Row>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <NewMediumDialog
        open={creating}
        taken={new Set(catalog.map((medium) => medium.id))}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          void navigate(`/mediums/${id}`);
        }}
      />
    </>
  );
}

/** The drag handle's dots. Decorative — the button's label carries the meaning. */
function GripGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 10 16" aria-hidden="true" focusable="false">
      {[3, 8, 13].map((y) => (
        <g key={y}>
          <circle cx="3" cy={y} r="1.3" fill="currentColor" />
          <circle cx="7" cy={y} r="1.3" fill="currentColor" />
        </g>
      ))}
    </svg>
  );
}
